import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Handshake,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  FilePdf,
  CurrencyEur,
  Clock,
  Car,
  User as UserIcon,
  ArrowRight,
  Check,
  X,
  Warning
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { usePermissions } from "@/hooks/usePermissions";

const STATUT_LABELS = {
  brouillon: { label: "Brouillon", color: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" },
  contrat_signe: { label: "Contrat signe", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" },
  en_preparation: { label: "En preparation", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
  en_annonce: { label: "En annonce", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  reserve: { label: "Reserve", color: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300" },
  vendu: { label: "Vendu", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" },
  restitue: { label: "Restitue", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" },
  expire: { label: "Expire", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" }
};

const COMMISSION_LABELS = { fixe: "Montant fixe", pourcentage: "Pourcentage", minimum_vendeur: "Minimum vendeur" };
const fmt = (v) => new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(v || 0);

/* ─── Deposant Tab ─── */
const DeposantTab = ({ deposants, fetchDeposants, can }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ nom: "", prenom: "", type_deposant: "particulier", entreprise: "", tva_numero: "", telephone: "", email: "", adresse: "", iban: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const s = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openCreate = () => { setEditId(null); setForm({ nom: "", prenom: "", type_deposant: "particulier", entreprise: "", tva_numero: "", telephone: "", email: "", adresse: "", iban: "", notes: "" }); setDialogOpen(true); };
  const openEdit = (d) => { setEditId(d.deposant_id); setForm({ ...d }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.nom) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/deposants/${editId}`, form);
      } else {
        await axios.post(`${API}/deposants`, form);
      }
      toast.success(editId ? "Deposant mis a jour" : "Deposant cree");
      setDialogOpen(false); fetchDeposants();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/deposants/${id}`);
      toast.success("Deposant supprime"); fetchDeposants();
    } catch { toast.error("Erreur"); }
  };

  const filtered = search ? deposants.filter(d => `${d.nom} ${d.prenom} ${d.entreprise || ""}`.toLowerCase().includes(search.toLowerCase())) : deposants;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="pl-8 h-9 text-sm" data-testid="deposant-search" />
        </div>
        {can("depot_vente", "create") && (
          <Button size="sm" onClick={openCreate} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="add-deposant-btn">
            <Plus size={14} className="mr-1" /> Nouveau deposant
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-400">Aucun deposant</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map(d => (
            <div key={d.deposant_id} className="card p-4 hover:shadow-md transition-all" data-testid={`deposant-card-${d.deposant_id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">{d.entreprise || `${d.prenom || ""} ${d.nom}`}</h3>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">{d.type_deposant === "societe" ? "Societe" : "Particulier"}</span>
                </div>
                <div className="flex gap-1">
                  {can("depot_vente", "update") && <button onClick={() => openEdit(d)} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"><PencilSimple size={14} className="text-slate-500" /></button>}
                  {can("depot_vente", "delete") && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button className="p-1 rounded hover:bg-red-50"><Trash size={14} className="text-red-400" /></button></AlertDialogTrigger>
                      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Supprimer ?</AlertDialogTitle><AlertDialogDescription>Irreversible.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Non</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(d.deposant_id)} className="bg-red-600">Oui</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
              <div className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
                {d.telephone && <div>{d.telephone}</div>}
                {d.email && <div>{d.email}</div>}
                {d.iban && <div className="truncate">IBAN: {d.iban}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={v => !v && setDialogOpen(false)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Modifier" : "Nouveau"} deposant</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="form-label">Nom *</Label><Input value={form.nom} onChange={e => s("nom", e.target.value)} data-testid="deposant-nom" /></div>
              <div><Label className="form-label">Prenom</Label><Input value={form.prenom} onChange={e => s("prenom", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="form-label">Type</Label>
                <Select value={form.type_deposant} onValueChange={v => s("type_deposant", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="particulier">Particulier</SelectItem><SelectItem value="societe">Societe</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="form-label">Entreprise</Label><Input value={form.entreprise} onChange={e => s("entreprise", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="form-label">Telephone</Label><Input value={form.telephone} onChange={e => s("telephone", e.target.value)} /></div>
              <div><Label className="form-label">Email</Label><Input value={form.email} onChange={e => s("email", e.target.value)} /></div>
            </div>
            <div><Label className="form-label">Adresse</Label><Input value={form.adresse} onChange={e => s("adresse", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="form-label">IBAN</Label><Input value={form.iban} onChange={e => s("iban", e.target.value)} /></div>
              <div><Label className="form-label">N TVA</Label><Input value={form.tva_numero} onChange={e => s("tva_numero", e.target.value)} /></div>
            </div>
            <div><Label className="form-label">Notes</Label><Textarea value={form.notes} onChange={e => s("notes", e.target.value)} rows={2} /></div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="deposant-save-btn">{saving ? "..." : "Enregistrer"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ─── Main Page ─── */
const DepotVente = () => {
  const { can } = usePermissions();
  const [tab, setTab] = useState("contrats");
  const [contrats, setContrats] = useState([]);
  const [deposants, setDeposants] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailContrat, setDetailContrat] = useState(null);
  const [reversementOpen, setReversementOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form
  const [form, setForm] = useState({
    deposant_id: "", vehicle_id: "", prix_affiche: "", prix_minimum_vendeur: "",
    commission_type: "fixe", commission_valeur: "", date_fin: "",
    frais_garde: 0, frais_preparation: 0, payeur_frais: "garage",
    conditions_garde: "", conditions_assurance: "", conditions_restitution: "", notes: ""
  });

  // Reversement form
  const [revMontant, setRevMontant] = useState("");
  const [revMode, setRevMode] = useState("virement");

  const sf = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const fetchContrats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/depot-vente?page=${page}&per_page=20`);
      setContrats(res.data.data || []);
      setTotalPages(res.data.pages || 1);
    } catch { /* */ }
    finally { setLoading(false); }
  }, [page]);

  const fetchDeposants = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/deposants?per_page=200`);
      setDeposants(res.data.data || []);
    } catch { /* */ }
  }, []);

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vehicles?per_page=200`);
      setVehicles(res.data.data || []);
    } catch { /* */ }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/depot-vente-stats`);
      setStats(res.data);
    } catch { /* */ }
  }, []);

  useEffect(() => { fetchContrats(); fetchDeposants(); fetchVehicles(); fetchStats(); }, [fetchContrats, fetchDeposants, fetchVehicles, fetchStats]);

  // Commission preview
  const prixAffiche = parseFloat(form.prix_affiche) || 0;
  const prixMin = parseFloat(form.prix_minimum_vendeur) || 0;
  const commVal = parseFloat(form.commission_valeur) || 0;
  let commPreview = 0, netPreview = 0;
  if (form.commission_type === "fixe") { commPreview = commVal; netPreview = prixAffiche - commVal; }
  else if (form.commission_type === "pourcentage") { commPreview = prixAffiche * commVal / 100; netPreview = prixAffiche - commPreview; }
  else if (form.commission_type === "minimum_vendeur") { netPreview = prixMin; commPreview = prixAffiche - prixMin; }

  const handleCreate = async () => {
    if (!form.deposant_id || !prixAffiche) { toast.error("Deposant et prix requis"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/depot-vente`, {
        ...form,
        prix_affiche: prixAffiche,
        prix_minimum_vendeur: prixMin,
        commission_valeur: commVal,
        date_fin: form.date_fin || undefined
      });
      toast.success("Contrat depot-vente cree");
      setDialogOpen(false); fetchContrats(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (contratId, newStatut) => {
    try {
      const body = { statut: newStatut };
      await axios.put(`${API}/depot-vente/${contratId}`, body);
      toast.success(`Statut mis a jour: ${STATUT_LABELS[newStatut]?.label}`);
      fetchContrats(); fetchStats();
    } catch { toast.error("Erreur"); }
  };

  const handleReversement = async () => {
    if (!detailContrat) return;
    try {
      await axios.post(`${API}/depot-vente/${detailContrat.contrat_id}/reversement`, {
        montant: parseFloat(revMontant) || detailContrat.net_deposant,
        mode_reversement: revMode
      });
      toast.success("Reversement enregistre");
      setReversementOpen(false); fetchContrats(); fetchStats();
    } catch { toast.error("Erreur"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/depot-vente/${id}`);
      toast.success("Contrat supprime"); fetchContrats(); fetchStats();
    } catch { toast.error("Erreur"); }
  };

  const downloadPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/depot-vente/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `contrat_dv_${id}.pdf`; a.click();
    } catch { toast.error("Erreur PDF"); }
  };

  const filtered = search
    ? contrats.filter(c => `${c.numero} ${c.deposant_nom} ${c.vehicle_description}`.toLowerCase().includes(search.toLowerCase()))
    : contrats;

  return (
    <div className="space-y-6" data-testid="depot-vente-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-slate-900 dark:text-slate-100">Depot-Vente</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Gestion des vehicules en depot-vente</p>
        </div>
        {can("depot_vente", "create") && (
          <Button onClick={() => { setForm({ deposant_id: "", vehicle_id: "", prix_affiche: "", prix_minimum_vendeur: "", commission_type: "fixe", commission_valeur: "", date_fin: "", frais_garde: 0, frais_preparation: 0, payeur_frais: "garage", conditions_garde: "", conditions_assurance: "", conditions_restitution: "", notes: "" }); setDialogOpen(true); }} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="new-dv-btn">
            <Plus size={16} className="mr-1.5" /> Nouveau contrat
          </Button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dv-stats">
          <div className="card p-3 text-center"><p className="text-xl font-bold text-violet-600">{stats.total}</p><p className="text-xs text-slate-500">Total</p></div>
          <div className="card p-3 text-center"><p className="text-xl font-bold text-blue-600">{stats.en_cours}</p><p className="text-xs text-slate-500">En cours</p></div>
          <div className="card p-3 text-center"><p className="text-xl font-bold text-emerald-600">{stats.vendus}</p><p className="text-xs text-slate-500">Vendus</p></div>
          <div className="card p-3 text-center"><p className="text-xl font-bold text-amber-600">{stats.expirant_bientot}</p><p className="text-xs text-slate-500">Expirent bientot</p></div>
          <div className="card p-3 text-center"><p className="text-xl font-bold text-red-600">{stats.reversements_en_attente}</p><p className="text-xs text-slate-500">Reversements</p></div>
          <div className="card p-3 text-center"><p className="text-xl font-bold text-violet-600">{fmt(stats.commission_attendue)}</p><p className="text-xs text-slate-500">Commission</p></div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList><TabsTrigger value="contrats">Contrats</TabsTrigger><TabsTrigger value="deposants">Deposants</TabsTrigger></TabsList>

        <TabsContent value="contrats" className="space-y-4 mt-4">
          <div className="card p-3">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="pl-8 h-9 text-sm" data-testid="dv-search" />
            </div>
          </div>

          {loading ? (
            <div className="card p-12 text-center"><div className="spinner h-8 w-8 mx-auto"></div></div>
          ) : filtered.length === 0 ? (
            <div className="card p-12 text-center">
              <Handshake size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <p className="text-sm text-slate-500">Aucun contrat depot-vente</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(c => {
                const st = STATUT_LABELS[c.statut] || STATUT_LABELS.brouillon;
                const isExpiringSoon = c.date_fin && new Date(c.date_fin) < new Date(Date.now() + 14 * 86400000) && !["vendu", "restitue", "expire"].includes(c.statut);
                return (
                  <div key={c.contrat_id} className="card p-4 hover:shadow-md transition-all border-l-4 border-l-violet-500" data-testid={`dv-card-${c.contrat_id}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
                          <Handshake size={20} className="text-violet-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">{c.numero}</span>
                            <span className="px-2 py-0.5 text-[10px] rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 font-medium">DEPOT-VENTE</span>
                            <span className={`px-2 py-0.5 text-[10px] rounded-full ${st.color}`}>{st.label}</span>
                            {isExpiringSoon && <span className="px-2 py-0.5 text-[10px] rounded-full bg-red-100 text-red-700 animate-pulse">Expire bientot</span>}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                            <span className="flex items-center gap-1"><UserIcon size={12} /> {c.deposant_nom || "N/A"}</span>
                            {c.vehicle_description && <span className="flex items-center gap-1"><Car size={12} /> {c.vehicle_description}</span>}
                            <span>{c.date_debut ? new Date(c.date_debut).toLocaleDateString("fr-BE") : ""}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="text-right">
                          <p className="font-bold text-sm text-slate-900 dark:text-slate-100">{fmt(c.prix_affiche)}</p>
                          <p className="text-[10px] text-violet-600">Commission: {fmt(c.commission_calculee)}</p>
                          {c.reversement_effectue && <p className="text-[10px] text-emerald-600 flex items-center gap-0.5"><Check size={10} /> Reverse</p>}
                          {c.statut === "vendu" && !c.reversement_effectue && <p className="text-[10px] text-red-500 flex items-center gap-0.5"><Warning size={10} /> A reverser</p>}
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => downloadPdf(c.contrat_id)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"><FilePdf size={16} className="text-red-500" /></button>
                          {can("depot_vente", "update") && c.statut !== "vendu" && (
                            <Select value={c.statut} onValueChange={v => handleStatusChange(c.contrat_id, v)}>
                              <SelectTrigger className="w-auto h-8 text-xs border-0 bg-transparent p-1"><ArrowRight size={14} className="text-slate-400" /></SelectTrigger>
                              <SelectContent>
                                {Object.entries(STATUT_LABELS).map(([k, v]) => (
                                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {can("depot_vente", "update") && c.statut === "vendu" && !c.reversement_effectue && (
                            <button onClick={() => { setDetailContrat(c); setRevMontant(String(c.net_deposant)); setReversementOpen(true); }} className="p-1.5 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20" data-testid={`reversement-${c.contrat_id}`}>
                              <CurrencyEur size={16} className="text-emerald-600" />
                            </button>
                          )}
                          {can("depot_vente", "delete") && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild><button className="p-1.5 rounded hover:bg-red-50"><Trash size={14} className="text-red-400" /></button></AlertDialogTrigger>
                              <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Supprimer ?</AlertDialogTitle><AlertDialogDescription>Le vehicule repassera en stock.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Non</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(c.contrat_id)} className="bg-red-600">Oui</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="deposants" className="mt-4">
          <DeposantTab deposants={deposants} fetchDeposants={fetchDeposants} can={can} />
        </TabsContent>
      </Tabs>

      {/* Create Contrat Dialog */}
      <Dialog open={dialogOpen} onOpenChange={v => !v && setDialogOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouveau contrat depot-vente</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="form-label">Deposant *</Label>
                <Select value={form.deposant_id || "none"} onValueChange={v => sf("deposant_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="dv-deposant"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Selectionner...</SelectItem>
                    {deposants.map(d => <SelectItem key={d.deposant_id} value={d.deposant_id}>{d.entreprise || `${d.prenom || ""} ${d.nom}`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Vehicule</Label>
                <Select value={form.vehicle_id || "none"} onValueChange={v => sf("vehicle_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="dv-vehicle"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Selectionner...</SelectItem>
                    {vehicles.map(v => <SelectItem key={v.vehicle_id} value={v.vehicle_id}>{v.marque} {v.modele} - {v.reference_interne}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="form-label">Prix affiche *</Label><Input type="number" value={form.prix_affiche} onChange={e => sf("prix_affiche", e.target.value)} data-testid="dv-prix-affiche" /></div>
              <div><Label className="form-label">Date fin contrat</Label><Input type="date" value={form.date_fin} onChange={e => sf("date_fin", e.target.value)} data-testid="dv-date-fin" /></div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="form-label">Type commission</Label>
                <Select value={form.commission_type} onValueChange={v => sf("commission_type", v)}>
                  <SelectTrigger data-testid="dv-commission-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixe">Montant fixe</SelectItem>
                    <SelectItem value="pourcentage">Pourcentage (%)</SelectItem>
                    <SelectItem value="minimum_vendeur">Minimum vendeur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">{form.commission_type === "pourcentage" ? "% Commission" : form.commission_type === "minimum_vendeur" ? "Minimum vendeur" : "Montant fixe"}</Label>
                <Input type="number" value={form.commission_type === "minimum_vendeur" ? form.prix_minimum_vendeur : form.commission_valeur} onChange={e => form.commission_type === "minimum_vendeur" ? sf("prix_minimum_vendeur", e.target.value) : sf("commission_valeur", e.target.value)} data-testid="dv-commission-value" />
              </div>
              <div>
                <Label className="form-label">Payeur frais</Label>
                <Select value={form.payeur_frais} onValueChange={v => sf("payeur_frais", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="garage">Garage</SelectItem><SelectItem value="deposant">Deposant</SelectItem><SelectItem value="inclus_commission">Inclus commission</SelectItem></SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview commission */}
            <div className="bg-violet-50 dark:bg-violet-900/10 rounded-xl p-4 border border-violet-200 dark:border-violet-800/30" data-testid="dv-commission-preview">
              <p className="text-xs text-violet-600 dark:text-violet-400 font-medium mb-2">Apercu financier</p>
              <div className="flex justify-between text-sm"><span>Prix affiche</span><span className="font-medium">{fmt(prixAffiche)}</span></div>
              <div className="flex justify-between text-sm mt-1"><span>Commission garage</span><span className="font-medium text-violet-700 dark:text-violet-400">{fmt(commPreview)}</span></div>
              <div className="flex justify-between text-sm mt-1 pt-1.5 border-t border-violet-200 dark:border-violet-700 font-bold"><span>Net deposant</span><span>{fmt(netPreview)}</span></div>
            </div>

            <div><Label className="form-label">Notes / Conditions</Label><Textarea value={form.notes} onChange={e => sf("notes", e.target.value)} rows={2} data-testid="dv-notes" /></div>

            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="dv-save-btn">{saving ? "..." : "Creer le contrat"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reversement Dialog */}
      <Dialog open={reversementOpen} onOpenChange={v => !v && setReversementOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reversement deposant</DialogTitle></DialogHeader>
          {detailContrat && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Contrat {detailContrat.numero} — Deposant: <strong>{detailContrat.deposant_nom}</strong>
              </p>
              <div className="bg-violet-50 dark:bg-violet-900/10 rounded-lg p-3 text-sm">
                <div className="flex justify-between"><span>Prix vente</span><span>{fmt(detailContrat.prix_vente_final || detailContrat.prix_affiche)}</span></div>
                <div className="flex justify-between"><span>Commission</span><span className="text-violet-600">{fmt(detailContrat.commission_calculee)}</span></div>
                <div className="flex justify-between font-bold mt-1 pt-1 border-t"><span>A reverser</span><span>{fmt(detailContrat.net_deposant)}</span></div>
              </div>
              <div><Label className="form-label">Montant</Label><Input type="number" value={revMontant} onChange={e => setRevMontant(e.target.value)} data-testid="rev-montant" /></div>
              <div>
                <Label className="form-label">Mode</Label>
                <Select value={revMode} onValueChange={setRevMode}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="virement">Virement</SelectItem><SelectItem value="especes">Especes</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setReversementOpen(false)}>Annuler</Button>
                <Button onClick={handleReversement} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rev-confirm-btn">Confirmer le reversement</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepotVente;
